import { createUseStyles } from "react-jss";
import type { Calendar, DayStatus, YearMonth } from "../../../../common/src";
import {
  firstOfMonth,
  getDayStatus,
  lastOfMonth,
  monthGrid,
  shiftMonth,
} from "../../../../common/src";
import type { VetBoatsTheme } from "../../theme";
import type { DaySelection } from "../../utils/selection";
import { isSelected } from "../../utils/selection";
import { formatDate, formatMonth } from "../../utils/format";
import { Button } from "../shared/Button";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const LEGEND_ORDER: readonly DayStatus[] = [
  "available",
  "mine",
  "reserved",
  "unavailable",
  "past",
];

const STATUS_LABELS: Record<DayStatus, string> = {
  available: "Open",
  mine: "Your reservation",
  reserved: "Reserved",
  unavailable: "Not open",
  past: "Past",
};

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 12,
  },
  range: { margin: 0, fontSize: 18, textAlign: "center", flex: 1 },
  months: { display: "grid", gap: 22 },
  monthTitle: { margin: "0 0 8px", fontSize: 16 },
  blank: { minHeight: 64, [theme.narrow]: { minHeight: 44 } },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
    gap: 4,
  },
  weekday: {
    textAlign: "center",
    fontSize: 12,
    fontWeight: 700,
    color: theme.color.muted,
    padding: "4px 0",
  },
  day: {
    position: "relative",
    minHeight: 64,
    padding: "6px 6px 4px",
    border: "2px solid transparent",
    borderRadius: 6,
    font: "inherit",
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    cursor: "default",
    [theme.narrow]: { minHeight: 44, padding: "4px 3px", fontSize: 13 },
  },
  number: { fontWeight: 700 },
  label: {
    fontSize: 11,
    lineHeight: 1.2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    [theme.narrow]: { display: "none" },
  },
  today: { boxShadow: `inset 0 0 0 1px ${theme.color.primary}` },
  selectable: {
    cursor: "pointer",
    "&:hover": { borderColor: `${theme.color.primary}80` },
    "&:focus-visible": {
      outline: `2px solid ${theme.color.accent}`,
      outlineOffset: 1,
    },
  },
  selected: {
    borderColor: theme.color.primary,
    background: `${theme.color.accent}33 !important`,
  },
  legend: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: "4px 12px",
    fontSize: 13,
    color: theme.color.muted,
  },
  available: {
    background: theme.day.available.bg,
    color: theme.day.available.fg,
  },
  mine: { background: theme.day.mine.bg, color: theme.day.mine.fg },
  reserved: { background: theme.day.reserved.bg, color: theme.day.reserved.fg },
  unavailable: {
    background: theme.day.unavailable.bg,
    color: theme.day.unavailable.fg,
  },
  past: { background: theme.day.past.bg, color: theme.day.past.fg },
  swatch: {
    display: "inline-block",
    width: 12,
    height: 12,
    borderRadius: 3,
    marginRight: 5,
    verticalAlign: "-1px",
    border: `1px solid ${theme.color.border}`,
  },
}));

/** How many consecutive months the calendar shows at once. */
export const VISIBLE_MONTHS = 2;

const visibleMonths = (first: YearMonth): YearMonth[] =>
  Array.from({ length: VISIBLE_MONTHS }, (_, i) => shiftMonth(first, i));

/** The date span the calendar needs data for, starting at `first`. */
export const calendarRange = (
  first: YearMonth
): { from: string; to: string } => ({
  from: firstOfMonth(first),
  to: lastOfMonth(shiftMonth(first, VISIBLE_MONTHS - 1)),
});

/** Color key for day statuses; shown in the calendar card's header. */
export const CalendarLegend = () => {
  const classes = useStyles();
  return (
    <div className={classes.legend}>
      {LEGEND_ORDER.map((s) => (
        <span key={s}>
          <span className={`${classes.swatch} ${classes[s]}`} />
          {STATUS_LABELS[s]}
        </span>
      ))}
    </div>
  );
};

interface MonthGridProps {
  month: YearMonth;
  calendar: Calendar | undefined;
  selection: DaySelection;
  onSelectDay: (date: string) => void;
}

const MonthGrid = ({
  month,
  calendar,
  selection,
  onSelectDay,
}: MonthGridProps) => {
  const classes = useStyles();
  const monthPrefix = firstOfMonth(month).slice(0, 7);
  const title = formatMonth(firstOfMonth(month));

  return (
    <section>
      <h3 className={classes.monthTitle}>{title}</h3>
      <div
        className={classes.grid}
        role="grid"
        aria-label={`${title} availability`}
      >
        {WEEKDAYS.map((d) => (
          <div key={d} className={classes.weekday} role="columnheader">
            {d}
          </div>
        ))}
        {monthGrid(month)
          .flat()
          .map((date) => {
            // Leave neighboring months' days blank so stacked months don't repeat dates.
            if (date.slice(0, 7) !== monthPrefix) {
              return <div key={date} className={classes.blank} aria-hidden />;
            }
            const status = calendar
              ? getDayStatus(date, calendar)
              : "unavailable";
            const reservation = calendar?.reservations.find(
              (r) => date >= r.startDate && date <= r.endDate
            );
            const selectable = calendar !== undefined && status === "available";
            const label =
              reservation?.reservedBy ?? (status === "available" ? "Open" : "");
            const classNames = [
              classes.day,
              classes[status],
              date === calendar?.today ? classes.today : "",
              selectable ? classes.selectable : "",
              isSelected(selection, date) ? classes.selected : "",
            ];
            return (
              <button
                key={date}
                type="button"
                role="gridcell"
                className={classNames.join(" ")}
                disabled={!selectable}
                aria-label={`${formatDate(date)}: ${STATUS_LABELS[status]}${reservation?.reservedBy ? ` (${reservation.reservedBy})` : ""}`}
                aria-pressed={isSelected(selection, date)}
                onClick={() => onSelectDay(date)}
              >
                <span className={classes.number}>{Number(date.slice(8))}</span>
                <span className={classes.label}>{label}</span>
              </button>
            );
          })}
      </div>
    </section>
  );
};

interface MonthCalendarProps {
  /** The first of the visible months. */
  month: YearMonth;
  onMonthChange: (month: YearMonth) => void;
  calendar: Calendar | undefined;
  selection: DaySelection;
  onSelectDay: (date: string) => void;
}

/** Consecutive months stacked vertically, with shared previous/next navigation. */
export const MonthCalendar = ({
  month,
  onMonthChange,
  calendar,
  selection,
  onSelectDay,
}: MonthCalendarProps) => {
  const classes = useStyles();
  const months = visibleMonths(month);
  const last = months.at(-1) ?? month;

  return (
    <div>
      <div className={classes.header}>
        <Button
          variant="secondary"
          small
          onClick={() => onMonthChange(shiftMonth(month, -1))}
          aria-label="Previous month"
        >
          ‹ Prev
        </Button>
        <h2 className={classes.range} aria-live="polite">
          {formatMonth(firstOfMonth(month))} – {formatMonth(firstOfMonth(last))}
        </h2>
        <Button
          variant="secondary"
          small
          onClick={() => onMonthChange(shiftMonth(month, 1))}
          aria-label="Next month"
        >
          Next ›
        </Button>
      </div>
      <div className={classes.months}>
        {months.map((m) => (
          <MonthGrid
            key={firstOfMonth(m)}
            month={m}
            calendar={calendar}
            selection={selection}
            onSelectDay={onSelectDay}
          />
        ))}
      </div>
    </div>
  );
};
